import React, { ReactElement, useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Col, Input, Row } from 'antd';
import { useGetFormConfig } from '../../hooks/useFormConfig';
import DatePicker from './FormInputDate/DatePicker';
import FormItem from './FormItem/FormItem';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { format, differenceInMinutes} from 'date-fns';
import { NewAdmissionState } from '../../../store/models/newAdmissionModel';
import { setDateApplied, setDaycareCase } from '../../../store/actions/newAdmissionAction';
import { ReactComponent as Calendar } from '../../../assets/images/svg/calendar.svg';
import { ReactComponent as ClearIcon } from '../../../assets/images/svg/clear-icon.svg';
import { convertToISOStrings } from '../../../modules/crm/inPatient/service/crmUtils';
import { PayerInitialState } from '../../../store/models/payerListModel';
interface DateValue {
  date: any;
  time: string;
}

const DatePickerInput = (props: any): ReactElement | null => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedAdmit, setSelectedAdmit] = useState<DateValue>({ date: '', time: '' });
  const [selectedDischarge, setSelectedDischarge] = useState<DateValue>({ date: '', time: '' });
  const [previousAdmit, setPreviousAdmit] = useState<DateValue>({ date: '', time: '' });
  const [previousDischarge, setPreviousDischarge] = useState<DateValue>({ date: '', time: '' });
  const {admissionFormVal} = useSelector((state: RootState): any => state?.newAdmission);
  const dispatch = useDispatch();

  const formConfig = useGetFormConfig(props);
  const { setval, setDifference, key, doaRightLimit, isDischargeCase, isHospitalizationCard, isDetailPage,
    doaLeftLimit, showNow, disabled, policyStartDate, policyEndDate, dodRightLimit, popConfirmCancel, isNewAdmissionPage,
    delayedIntimationCancel, isEasyDischargeCase, delayModalVisible, lOSgtWarning, claimForm, isSettlement,
    isContinuePopVisible, isDuplicateClaim, onFocus, onBlur, disableDOD,
  } = formConfig;
  const { isMinimize, isDateApplied } = useSelector((state: RootState): NewAdmissionState => state.newAdmission);
  const { payer } = useSelector((state: RootState): PayerInitialState => state.payerList);

  const datePickerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent): void => {
    if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node) && !(delayModalVisible || lOSgtWarning ||
     isContinuePopVisible || isDuplicateClaim)) {
      // Close DatePicker when clicking outside
      setIsDatePickerOpen(false);
      if (!isDateApplied && isDatePickerOpen) {
        setSelectedAdmit({ date: previousAdmit?.date, time: previousAdmit?.time });
        setSelectedDischarge({ date: previousDischarge?.date, time: previousDischarge?.time });
      }
    }
  };

  useEffect((): any => {
    if (isDatePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDatePickerOpen, delayModalVisible, lOSgtWarning, isContinuePopVisible, isDuplicateClaim]);

  useEffect((): void => {
    processDate(0, setSelectedAdmit);
    processDate(1, setSelectedDischarge);

  }, [admissionFormVal, isMinimize]);

  const processDate = (index: number, setSelectedFunction: any): void => {
      const { treatmentDetail, customerDetail } = admissionFormVal;
      const detailDate = treatmentDetail?.stayLength?.[index] || customerDetail?.stayLength?.[index];
      const stayLength = claimForm?.getFieldValue('stayLength');
      const stayLengthISO = !isDetailPage ? convertToISOStrings(stayLength) : null;
      const stayLengthDate = stayLengthISO?.[index];

      if (detailDate || stayLengthDate) {
        const dateToUse = stayLengthDate ? new Date(stayLengthDate) : new Date(detailDate);
        if (dateToUse?.toString() !== 'Invalid Date') {
          if (dateToUse?.toString() !== 'Invalid Date') {
          setSelectedFunction({
              date: format(dateToUse, 'dd/MMM/yyyy'),
              time: format(dateToUse, 'hh:mm aa'),
            });
        }
        }
      }
    };

  useEffect((): any => {
    if (payer?.isOnMailPayer && isNewAdmissionPage) {
      handleClearDates();
      processDate(0, setSelectedAdmit);
      processDate(1, setSelectedDischarge);
    }
    }, [payer]);

  useEffect((): any => {
    if (isDischargeCase) {
      setSelectedDischarge((prev: DateValue): any => ({
        date: format(new Date(), 'dd/MMM/yyyy'),
        time: format(new Date(), 'hh:mm aa'),
      }));
    }
  }, [isDischargeCase]);

  useEffect((): void => {
    // checking only time because LOS depends on the time
    if (selectedAdmit?.time !== '' && selectedDischarge?.time !== '') {
      calculateDaysDifference();
      setval(selectedAdmit?.date, selectedDischarge?.date, selectedAdmit?.time, selectedDischarge?.time);
    } else {
      setDifference(0);
    }
  }, [selectedAdmit, selectedDischarge]);

  const calculateDaysDifference = (): void => {
    if (!selectedAdmit || !selectedDischarge) {
      return;
    }

    const admitDateTime = new Date(`${selectedAdmit?.date} ${selectedAdmit?.time}`);
    const dischargeDateTime = new Date(`${selectedDischarge?.date} ${selectedDischarge?.time}`);

    const minutesDifference = differenceInMinutes(dischargeDateTime, admitDateTime);
    const daysDifference = Math.ceil(minutesDifference / (24 * 60));
    if (daysDifference === 1) {
      dispatch(setDaycareCase(true));
    } else {
      dispatch(setDaycareCase(false));
    }
    setDifference(daysDifference);
  };

  const handleDateChange = useCallback(
    (
      admitDate?: string,
      dischargeDate?: string,
      admitTime?: string,
      dischargeTime?: string,
    ): void => {
      setSelectedAdmit((prev: DateValue): any => ({
        date: admitDate !== undefined ? admitDate : prev.date,
        time: admitTime !== undefined ? admitTime : prev.time,
      }));
      setSelectedDischarge((prev: DateValue): any => ({
        date: dischargeDate !== undefined ? dischargeDate : prev.date,
        time: dischargeTime !== undefined ? dischargeTime : prev.time,
      }));
      setval(admitDate, dischargeDate, admitTime, dischargeTime);
  }, [setval]);

  useEffect((): void => {
    if (isDatePickerOpen) {
      onFocus && onFocus();
      dispatch(setDateApplied(false));
    } else {
      onBlur && onBlur();
    }
  }, [isDatePickerOpen]);

  const handleOpenDatePicker = (): void => {
    setPreviousAdmit({ ...selectedAdmit });
    setPreviousDischarge({ ...selectedDischarge });
    setIsDatePickerOpen(true);
    dispatch(setDateApplied(false));
  };

  const handleClearDates = (): void => {
    setSelectedAdmit({ date: '', time: '' });
    if (!disableDOD) {
      setSelectedDischarge({ date: '', time: '' });
    }
    claimForm.setFieldsValue({ 'stayLength': undefined });
    setDifference(0);
  };

   return (
    <FormItem formConfig={formConfig}>
      <Row className={`custom-amount-range ml-0 ${disabled ? 'disabled' : ''}`} gutter={24} onClick={handleOpenDatePicker}>
        <Col md={24}>
          <Row gutter={24}>
            <Col md={10}>
              <Input
                type="text"
                placeholder="Date of Admission"
                id="startDate"
                className="combined-date-field"
                value={selectedAdmit?.date || selectedAdmit?.time ? `${selectedAdmit?.date} ${selectedAdmit?.time}`.trim() : ''}
                readOnly={true}
                disabled={disabled}
              />
            </Col>
            <Col md={2}><ArrowRightOutlined className="custom-swap-right-icon" /></Col>
            <Col md={10}>
              <Input
                type="text"
                placeholder="Date of Discharge"
                id="endDate"
                className="combined-date-field"
                value={selectedDischarge?.date || selectedDischarge?.time ? `${selectedDischarge?.date} ${selectedDischarge?.time}`.trim() : ''}
                readOnly={true}
                disabled={disableDOD || disabled}
              />
            </Col>
            <Col md={2}>
              <span data-testid="address-clear" className="clear-btn">
                {selectedAdmit?.date && selectedDischarge?.date ? (
                  <ClearIcon
                    className={`close-icon ${disabled ? 'disabled' : ''}`}
                    onClick={(e: any): void => {
                      e.stopPropagation();
                      if (!disabled) {
                        handleClearDates();
                      }
                    }}
                  />
                ) : (
                  <Calendar
                    className={`close-icon ${disabled ? 'disabled' : ''}`}
                    onClick={(e: any): void => {
                      e.stopPropagation();
                      if (!disabled) {
                        setIsDatePickerOpen(!isDatePickerOpen);
                      }
                    }}
                  />
                )}
              </span>
            </Col>
          </Row>
        </Col>
      </Row>
      {isDatePickerOpen && !disabled && (
        <div ref ={datePickerRef}className={`${isHospitalizationCard ? '' : 'datePickerOverlay'}`}>
          <DatePicker
            setSelect={handleDateChange}
            doaRightLimit={doaRightLimit}
            doaLeftLimit={doaLeftLimit}
            dodLimit={dodRightLimit}
            showNow={showNow}
            setIsDatePickerOpen={setIsDatePickerOpen}
            key={key}
            initialAdmitDate={selectedAdmit?.date ? new Date(selectedAdmit?.date) : undefined}
            initialDischargeDate={selectedDischarge?.date ? new Date(selectedDischarge?.date) : undefined}
            initialAdmitTime={selectedAdmit?.time || undefined}
            initialDischargeTime={selectedDischarge?.time || undefined}
            policyStartDate={policyStartDate}
            policyEndDate={policyEndDate}
            isDischargeCase={isDischargeCase}
            isSettlement={isSettlement}
            previousAdmitDate={previousAdmit?.date ? new Date(previousAdmit?.date) : undefined}
            previousDischargeDate={previousDischarge?.date ? new Date(previousDischarge?.date) : undefined}
            previousAdmitTime={previousAdmit?.time || undefined}
            previousDischargeTime={previousDischarge?.time || undefined}
            popConfirmCancel={popConfirmCancel}
            delayedIntimationCancel={delayedIntimationCancel}
            isEasyDischargeCase={isEasyDischargeCase}
            isDatePickerOpen = {isDatePickerOpen}
            disableDOD = {disableDOD}
          />
        </div>
      )}
    </FormItem>
  );
};

export default DatePickerInput;
